import { useAuth } from '@teko/auth-react';
import { isServerless } from 'helpers';
import { useTenantConfig } from 'hooks';
import { useEffect, useLayoutEffect } from 'react';
import { useChatbotStore } from 'stores/chatbotStore';

const DEFAULT_SIDEBAR_WIDTH = 400;

const useChatbotConfig = (
  setIsEnabled: (v: boolean) => void,
  setSidebarConfig: (opts: {
    sidebarWidth: number;
    primaryColor?: string;
  }) => void,
) => {
  const { chatbotConfig } = useTenantConfig();
  const { user } = useAuth();

  useEffect(() => {
    // Tính năng đang internal testing phục vụ user mail @teko.vn
    const isTekoEmail = user?.email?.endsWith('@teko.vn') ?? false;
    setIsEnabled(!!chatbotConfig && isServerless() && isTekoEmail);
    setSidebarConfig({
      sidebarWidth: chatbotConfig?.sidebarWidth ?? DEFAULT_SIDEBAR_WIDTH,
      primaryColor: chatbotConfig?.primaryColor,
    });
  }, [chatbotConfig, setIsEnabled, setSidebarConfig, user]);
};

// Tính vị trí sidebar dựa trên nav height. Ban đầu sidebar nằm dưới topbar + nav,
// khi scroll topbar ẩn đi thì tự tính lại. Kết quả được ghi vào CSS variables để
// sidebar dùng trong layout.
const useChatbotNavOffset = (enabled: boolean) => {
  useLayoutEffect(() => {
    if (!enabled) return;
    const navEl = document.getElementById('mainNavigationBar');
    if (!navEl) return;

    const update = () => {
      const navRect = navEl.getBoundingClientRect();
      const navHeight = navEl.offsetHeight;
      document.documentElement.style.setProperty(
        '--chatbot-nav-height',
        `${navHeight}px`,
      );
      // navRect.top + navHeight = cạnh dưới nav tính từ đỉnh viewport.
      // Math.max với navHeight để tránh giá trị âm khi nav không sticky và scroll ra ngoài màn hình.
      const navBottomOffset = Math.max(navHeight, navRect.top + navHeight);
      document.documentElement.style.setProperty(
        '--chatbot-total-offset',
        `${navBottomOffset}px`,
      );
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(navEl);
    window.addEventListener('scroll', update, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update);
    };
  }, [enabled]);
};

export const ChatbotStoreSync = (): null => {
  const { isEnabled, setIsEnabled, setSidebarConfig } = useChatbotStore();
  useChatbotConfig(setIsEnabled, setSidebarConfig);
  useChatbotNavOffset(isEnabled);
  return null;
};
